import {
  IRegistryParameters,
  IComponentBundleLoader,
  IHostContext,
} from '../../registry.types';
import { createException, RegistryErrorCode } from '../../errors/RegistryError';
import { filterDependencyAssets } from '../../toolbox/assets';

import { createStaticComponentsLoaders } from './createStaticComponentsLoaders';
import { registerComponents } from './registerComponents';

export interface IEagerRegistryParameters {
  bundleLoaders: Record<string, IComponentBundleLoader>;
  contexts: Array<IHostContext>;
}

export function createEagerEntry<TComponentModel>({
  bundleLoaders,
  contexts,
}: IEagerRegistryParameters) {
  return async (params: IRegistryParameters<TComponentModel, never>) => {
    const tasks = contexts.map(async context => {
      const { library, manifest } = context;

      const loadBundle = bundleLoaders[library.artifactId];

      if (manifest && manifest.url && manifest.id) {
        if (manifest.deps) {
          await Promise.all([
            ...manifest.deps.map(dep =>
              loadBundle({
                url: dep,
              }),
            ),
            loadBundle({
              assets: filterDependencyAssets(manifest.assets),
            }),
          ]);
        }

        const bundle = await loadBundle<TComponentModel>({
          id: manifest.id,
          url: manifest.url,
          assets: manifest.assets,
        });

        if (!bundle || !bundle.components) {
          throw createException(
            'Incorrect eager bundle. Bundle does contain "components" export',
            RegistryErrorCode.IncorrectBundleExport,
          )
            .withLibrary(library.artifactId)
            .withUrl(manifest.url);
        }

        const components = createStaticComponentsLoaders<TComponentModel>({
          components: bundle.components,
          context,
        });

        registerComponents<TComponentModel, never>(components, context, {
          registerComponent: params.registerComponent,
          registerLibrary: params.registerLibrary,
        });
      }
    });

    await Promise.all(tasks);
  };
}
